import React, { useState, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { cn } from '@/lib/utils';
import { 
  ThumbsUp, 
  ThumbsDown, 
  Flag, 
  Eye, 
  Share,
  Grid3X3,
  Image as ImageIcon,
  Building2,
  Calendar,
  Tags,
  Clock,
  CheckCircle,
  XCircle
} from 'lucide-react';
import type { ApprovalItem, FranchiseRegion } from '@/types/gallery';

interface ApprovalSwipeCardProps {
  item: ApprovalItem;
  onApprove: (item: ApprovalItem) => void;
  onReject: (item: ApprovalItem) => void;
  onFlag: (item: ApprovalItem) => void;
  onView?: (item: ApprovalItem) => void;
  onShare?: (item: ApprovalItem) => void;
  className?: string;
}

interface DragState {
  isDragging: boolean;
  dragDelta: number;
  startX: number;
}

const franchiseRegions: { value: FranchiseRegion; label: string }[] = [
  { value: 'HQ', label: 'Headquarters' },
  { value: 'toronto', label: 'Toronto' },
  { value: 'hamilton', label: 'Hamilton' },
  { value: 'mississauga', label: 'Mississauga' },
  { value: 'brampton', label: 'Brampton' },
  { value: 'north_york', label: 'North York' },
  { value: 'scarborough', label: 'Scarborough' },
  { value: 'london', label: 'London' },
  { value: 'general', label: 'General' }
];

const ApprovalSwipeCard: React.FC<ApprovalSwipeCardProps> = ({
  item,
  onApprove,
  onReject,
  onFlag,
  onView,
  onShare,
  className
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [dragState, setDragState] = useState<DragState>({
    isDragging: false,
    dragDelta: 0,
    startX: 0
  });
  const [actionState, setActionState] = useState<'none' | 'approve' | 'reject' | 'flag'>('none');

  const handleDragStart = (e: React.TouchEvent | React.MouseEvent) => {
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    setDragState({
      isDragging: true,
      dragDelta: 0,
      startX: clientX
    });
  };

  const handleDragMove = (e: React.TouchEvent | React.MouseEvent) => {
    if (!dragState.isDragging) return;
    
    e.preventDefault();
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const deltaX = clientX - dragState.startX;
    
    setDragState(prev => ({
      ...prev,
      dragDelta: deltaX
    }));
  };

  const handleDragEnd = () => {
    if (!dragState.isDragging) return;

    const threshold = 100;
    const { dragDelta } = dragState;

    if (Math.abs(dragDelta) > threshold) {
      if (dragDelta > 0) {
        // Swipe right - approve
        setActionState('approve');
        setTimeout(() => {
          onApprove(item);
          setActionState('none');
        }, 300);
      } else {
        // Swipe left - reject
        setActionState('reject');
        setTimeout(() => {
          onReject(item);
          setActionState('none');
        }, 300);
      }
    }

    setDragState({
      isDragging: false,
      dragDelta: 0,
      startX: 0
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-800"><XCircle className="w-3 h-3 mr-1" />Rejected</Badge>;
      case 'under_review':
        return <Badge className="bg-blue-100 text-blue-800"><Flag className="w-3 h-3 mr-1" />Under Review</Badge>;
      default:
        return <Badge className="bg-yellow-100 text-yellow-800"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
    }
  };

  const getRegionBadge = (region: FranchiseRegion) => {
    const regionInfo = franchiseRegions.find(r => r.value === region);
    return <Badge variant="outline">{regionInfo?.label || region}</Badge>;
  };

  const getBgColor = () => {
    if (actionState === 'approve') return 'bg-green-50 dark:bg-green-900/20';
    if (actionState === 'reject') return 'bg-red-50 dark:bg-red-900/20';
    if (actionState === 'flag') return 'bg-yellow-50 dark:bg-yellow-900/20';
    return '';
  };

  const getTransformStyle = () => {
    if (dragState.isDragging) {
      return {
        transform: `translateX(${dragState.dragDelta}px) rotate(${dragState.dragDelta * 0.05}deg)`,
        transition: 'none'
      };
    }
    
    if (actionState === 'approve') {
      return {
        transform: 'translateX(100%) rotate(5deg)',
        transition: 'transform 0.3s ease'
      };
    }
    
    if (actionState === 'reject') {
      return {
        transform: 'translateX(-100%) rotate(-5deg)',
        transition: 'transform 0.3s ease'
      };
    }
    
    return {
      transform: 'translateX(0) rotate(0)',
      transition: 'transform 0.3s ease'
    };
  };

  return (
    <div className={cn("relative", className)}>
      <Card 
        ref={cardRef}
        className={cn(
          "overflow-hidden cursor-grab active:cursor-grabbing hover:shadow-lg transition-shadow",
          getBgColor()
        )}
        style={getTransformStyle()}
        onTouchStart={handleDragStart}
        onTouchMove={handleDragMove}
        onTouchEnd={handleDragEnd}
        onMouseDown={handleDragStart}
        onMouseMove={dragState.isDragging ? handleDragMove : undefined}
        onMouseUp={handleDragEnd}
        onMouseLeave={handleDragEnd}
      >
        <CardContent className="p-0">
          <div className="flex flex-col sm:flex-row">
            {/* Image Section */}
            <div className="w-full sm:w-64 flex-shrink-0">
              <AspectRatio ratio={16 / 10} className="bg-muted">
                {item.image_url ? (
                  <img
                    src={item.image_url}
                    alt={item.title}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      e.currentTarget.parentElement?.querySelector('.fallback-icon')?.classList.remove('hidden');
                    }}
                  />
                ) : null}
                <div className="fallback-icon hidden absolute inset-0 flex items-center justify-center">
                  {item.type === 'gallery' ? (
                    <Grid3X3 className="h-12 w-12 text-gray-400" />
                  ) : (
                    <ImageIcon className="h-12 w-12 text-gray-400" />
                  )}
                </div>
              </AspectRatio>
            </div>

            {/* Content Section */}
            <div className="flex-1 p-4 space-y-3">
              <div className="space-y-2">
                <div className="flex items-start justify-between">
                  <h3 className="font-semibold text-lg line-clamp-2">{item.title}</h3>
                  <Badge variant={item.type === 'gallery' ? 'default' : 'secondary'} className="ml-2 flex-shrink-0">
                    {item.type === 'gallery' ? 'Gallery' : 'Image'}
                  </Badge>
                </div>
                
                {item.description && (
                  <p className="text-sm text-gray-600 line-clamp-3">
                    {item.description}
                  </p>
                )}
              </div>

              {/* Metadata */}
              <div className="flex flex-wrap gap-2">
                {getRegionBadge(item.franchise_region)}
                {item.job_number && (
                  <Badge variant="outline" className="text-xs">
                    Job #{item.job_number}
                  </Badge>
                )}
                {item.service_name && (
                  <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700">
                    {item.service_name}
                  </Badge>
                )}
                {getStatusBadge(item.review_status)}
              </div>

              {/* Date */}
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <Calendar className="h-3 w-3" />
                {new Date(item.created_at).toLocaleDateString()}
              </div>

              {/* Action Buttons - Desktop */}
              <div className="hidden sm:flex items-center justify-between pt-2 border-t">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onView?.(item)}
                >
                  <Eye className="w-4 h-4 mr-1" />
                  View
                </Button>
                
                <div className="flex space-x-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-green-600 hover:bg-green-50"
                    onClick={() => onApprove(item)}
                  >
                    <ThumbsUp className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-red-600 hover:bg-red-50"
                    onClick={() => onReject(item)}
                  >
                    <ThumbsDown className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-orange-600 hover:bg-orange-50"
                    onClick={() => onFlag(item)}
                  >
                    <Flag className="w-4 h-4" />
                  </Button>
                  {onShare && item.type === 'gallery' && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-blue-600 hover:bg-blue-50"
                      onClick={() => onShare(item)}
                    >
                      <Share className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Swipe Overlays */}
      {dragState.isDragging && (
        <>
          {dragState.dragDelta < -50 && (
            <div className="absolute inset-0 bg-red-500/20 flex items-center justify-center z-10 rounded-lg">
              <div className="text-center">
                <ThumbsDown className="h-12 w-12 text-red-500 mx-auto mb-2" />
                <span className="text-red-700 font-semibold">Reject</span>
              </div>
            </div>
          )}
          {dragState.dragDelta > 50 && (
            <div className="absolute inset-0 bg-green-500/20 flex items-center justify-center z-10 rounded-lg">
              <div className="text-center">
                <ThumbsUp className="h-12 w-12 text-green-500 mx-auto mb-2" />
                <span className="text-green-700 font-semibold">Approve</span>
              </div>
            </div>
          )}
        </>
      )}

      {/* Mobile Action Buttons */}
      <div className="sm:hidden flex justify-center space-x-4 mt-4">
        <Button
          size="lg"
          variant="outline"
          className="text-red-600 hover:bg-red-50 flex-1"
          onClick={() => onReject(item)}
        >
          <ThumbsDown className="w-5 h-5 mr-2" />
          Reject
        </Button>
        <Button
          size="lg"
          variant="outline"
          className="text-orange-600 hover:bg-orange-50"
          onClick={() => onFlag(item)}
        >
          <Flag className="w-5 h-5" />
        </Button>
        <Button
          size="lg"
          variant="outline"
          className="text-green-600 hover:bg-green-50 flex-1"
          onClick={() => onApprove(item)}
        >
          <ThumbsUp className="w-5 h-5 mr-2" />
          Approve
        </Button>
      </div>
    </div>
  );
};

export default ApprovalSwipeCard;